import React from 'react';
import { useAppStore } from '../../stores/appStore';
import { useQueueStore } from '../../stores/queueStore';
import { usePlayerStore } from '../../stores/playerStore';
import { useAuthStore } from '../../stores/authStore';
import { 
  Bars3Icon, 
  BellIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

interface HeaderProps {
  onMenuToggle: () => void;
  isMobileMenuOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle, isMobileMenuOpen }) => {
  const { isInitialized, globalError, clearAllErrors } = useAppStore();
  const { queue, currentPlayers, canStartMatch } = useQueueStore();
  const { players } = usePlayerStore();
  const { username, logout } = useAuthStore();

  const getStatusInfo = () => {
    if (!isInitialized) {
      return {
        status: 'initializing',
        message: '系统初始化中...',
        color: 'text-yellow-600 bg-yellow-50',
        icon: <ExclamationTriangleIcon className="w-5 h-5" />
      };
    }

    if (globalError) {
      return {
        status: 'error',
        message: '系统错误',
        color: 'text-red-600 bg-red-50',
        icon: <ExclamationTriangleIcon className="w-5 h-5" />
      };
    }

    if (players.length < 4) {
      return {
        status: 'warning',
        message: '人数不足',
        color: 'text-orange-600 bg-orange-50',
        icon: <ExclamationTriangleIcon className="w-5 h-5" />
      };
    }

    if (canStartMatch()) {
      return {
        status: 'ready',
        message: '可以开始比赛',
        color: 'text-green-600 bg-green-50',
        icon: <CheckCircleIcon className="w-5 h-5" />
      };
    }

    return {
      status: 'normal',
      message: '系统正常',
      color: 'text-blue-600 bg-blue-50',
      icon: <CheckCircleIcon className="w-5 h-5" />
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="fixed top-0 left-0 right-0 z-30 bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Left side */}
          <div className="flex items-center">
            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              onClick={onMenuToggle}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            {/* Logo and title */}
            <div className="flex items-center ml-4 lg:ml-0">
              <UserGroupIcon className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  轮换比赛管理系统
                </h1>
                <p className="text-sm text-gray-500 hidden sm:block">
                  智能配对 • 公平轮换 • 数据统计
                </p>
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {/* System status */}
            <div className={`hidden sm:flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
              {statusInfo.icon}
              <span>{statusInfo.message}</span>
            </div>

            {/* Quick stats */}
            <div className="hidden md:flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <span className="font-medium">{players.length}</span>
                <span>玩家</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="font-medium">{currentPlayers.length}</span>
                <span>参赛中</span>
              </div>
              {queue && (
                <div className="flex items-center space-x-1">
                  <span className="font-medium">{queue.currentPosition}</span>
                  <span>轮次</span>
                </div>
              )}
            </div>

            {/* User info and logout */}
            <div className="flex items-center space-x-3 border-l border-gray-200 pl-4">
              <div className="hidden sm:block text-sm text-gray-700">
                <span className="font-medium">{username}</span>
              </div>
              <button
                onClick={logout}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="登出"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span className="hidden sm:inline">登出</span>
              </button>
            </div>

            {/* Error notification */}
            {globalError && (
              <button
                onClick={clearAllErrors}
                className="relative p-2 text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-full"
                title="点击清除错误"
              >
                <BellIcon className="h-6 w-6" />
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile status bar */}
      <div className="sm:hidden border-t border-gray-200 px-4 py-2">
        <div className={`flex items-center justify-between text-sm ${statusInfo.color}`}>
          <div className="flex items-center space-x-2">
            {statusInfo.icon}
            <span className="font-medium">{statusInfo.message}</span>
          </div>
          <div className="flex items-center space-x-3 text-gray-600">
            <span>{players.length} 玩家</span>
            <span>{currentPlayers.length} 参赛</span>
          </div>
        </div>
      </div>

      {/* Global error banner */}
      {globalError && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-red-800">
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span className="text-sm font-medium">系统错误：{globalError}</span>
            </div>
            <button
              onClick={clearAllErrors}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              清除
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;